/* eslint-disable react/prop-types */
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert, AlertDescription } from "@/components/ui/Alert";
import { ShoppingBag, Printer, AlertCircle } from "lucide-react";
import { getPrinterService } from "../../services/printer/MockPrinterService";

const calculateProductSummary = (transactions) => {
  return transactions
    .reduce((products, transaction) => {
      transaction.items.forEach((item) => {
        const existingProduct = products.find((p) => p.name === item.name);
        if (existingProduct) {
          existingProduct.quantity += item.quantity;
          existingProduct.total += item.price * item.quantity;
        } else {
          products.push({
            name: item.name,
            quantity: item.quantity,
            price: item.price,
            total: item.price * item.quantity,
          });
        }
      });
      return products;
    }, [])
    .sort((a, b) => b.total - a.total);
};

const ProductSummaryTable = ({ products, metrics }) => (
  <table className="w-full">
    <thead className="bg-orange-50">
      <tr className="border-b border-orange-200">
        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
          Producto
        </th>
        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">
          Cantidad
        </th>
        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">
          Precio Unit.
        </th>
        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">
          Total
        </th>
      </tr>
    </thead>
    <tbody className="divide-y divide-orange-100">
      {products.map((product, index) => (
        <tr key={index} className="hover:bg-orange-50">
          <td className="px-4 py-3 text-sm text-gray-900">{product.name}</td>
          <td className="px-4 py-3 text-sm text-center text-gray-900">
            {product.quantity}
          </td>
          <td className="px-4 py-3 text-sm text-right text-gray-900">
            ${product.price.toFixed(2)}
          </td>
          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
            ${product.total.toFixed(2)}
          </td>
        </tr>
      ))}
    </tbody>
    <tfoot className="bg-orange-50 border-t border-orange-200">
      <tr>
        <td
          colSpan="3"
          className="px-4 py-3 text-sm font-medium text-gray-600 text-right"
        >
          Total del Día:
        </td>
        <td className="px-4 py-3 text-right font-bold text-orange-600">
          ${metrics?.totalSales.toFixed(2)}
        </td>
      </tr>
    </tfoot>
  </table>
);

const PrintButtons = ({ printers, onPrint }) => (
  <div className="flex gap-2">
    {printers.map((printer) => (
      <Button
        key={printer.id}
        onClick={() => onPrint(printer.id)}
        className="bg-orange hover:bg-orange-100 text-orange-600"
        size="sm"
      >
        <Printer className="h-4 w-4 mr-2" />
        Imprimir en {printer.name}
      </Button>
    ))}
  </div>
);

const ProductSummaryCard = ({ sales, metrics, printers }) => {
  const printerService = getPrinterService(); // Obtener la instancia del servicio
  const [printError, setPrintError] = useState(null);

  const handlePrintSummary = async (printerId) => {
    try {
      setPrintError(null);
      await printerService.printProductSummary(printerId, sales, metrics);
    } catch (error) {
      setPrintError(error.message);
    }
  };

  const products = calculateProductSummary(sales.transactions);

  return (
    <Card className="border-orange-200">
      <CardHeader className="border-b border-orange-200 bg-orange-500">
        <CardTitle className="text-2xl text-white flex items-center justify-between">
          <div className="flex items-center">
            <ShoppingBag className="mr-2 h-6 w-6" />
            Resumen por Producto
          </div>
          <PrintButtons printers={printers} onPrint={handlePrintSummary} />
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {printError && (
          <Alert variant="destructive" className="m-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{printError}</AlertDescription>
          </Alert>
        )}
        <div className="overflow-x-auto">
          <ProductSummaryTable products={products} metrics={metrics} />
        </div>
      </CardContent>
    </Card>
  );
};

export default ProductSummaryCard;
